import type { Vfs, OpenFile } from '@mimir/common/types/vfs';
import type { IpcVfs } from '@mimir/common/types/ipcVfs';
import type { VfsPortClient } from './ipcVfsPort';

// This is a basic wrapper for the IpcVfs to conform with the Vfs interface.
// It is meant to be applied when in the electron context that the vfs will
// be used from.
//
// For more information see the comment above the IpcVfs interface definition.

function isSharedArrayBufferBacked(buf: Uint8Array): buf is Uint8Array<SharedArrayBuffer> {
  return typeof SharedArrayBuffer !== 'undefined' && buf.buffer instanceof SharedArrayBuffer;
}

export function createIpcVfsWrapper(
  ipcVfsInternal: IpcVfs,
  portClientPromise: Promise<VfsPortClient | null>,
): Vfs {
  const internal = ipcVfsInternal;

  let maybePortClient: VfsPortClient | null = null;
  void portClientPromise.then(
    (client) => { maybePortClient = client; },
    () => { maybePortClient = null; },
  );

  return {
    async open(path: string, flags: string): Promise<OpenFile> {
      const internalOpenFile = await internal.open(path, flags);
      const openFileId = internalOpenFile.openFileId;
      return {
        async read(buf: Uint8Array, bufOffset: number, count: number, fileOffset: number): Promise<number> {
          if (bufOffset + count > buf.length) {
            throw new Error('Buffer out of range');
          }

          if (maybePortClient !== null && isSharedArrayBufferBacked(buf)) {
            const view = buf.subarray(bufOffset, bufOffset + count);
            return await maybePortClient.read(openFileId, view, fileOffset);
          }

          // The buffer reference does not make it across the context bridge, so instead we have to get
          // the buffer as a return value to get the data
          const data: Uint8Array = await internalOpenFile.read(count, fileOffset);
          if (data.length + bufOffset > buf.length) {
            throw new Error('Buffer out of range');
          }
          buf.set(data, bufOffset);
          return data.length;
        },
        async write(buf: Uint8Array, bufOffset: number, count: number, fileOffset: number): Promise<number> {
          if (bufOffset + count > buf.length) {
            throw new Error('Buffer out of range');
          }

          if (maybePortClient !== null && isSharedArrayBufferBacked(buf)) {
            // subarray, since it is a shared buffer
            const view = buf.subarray(bufOffset, bufOffset + count);
            return await maybePortClient.write(openFileId, view, fileOffset);
          }

          // slice, using subarray would structured-clone the whole underlying buffer
          return await internalOpenFile.write(buf.slice(bufOffset, bufOffset + count), fileOffset);
        },
        close: internalOpenFile.close,
      };
    },
    openDir: internal.openDir,
    rename: internal.rename,
    stat: internal.stat,
    unlink: internal.unlink,
    mkdir: internal.mkdir,
    rmdir: internal.rmdir,
  };
}

